/**
 * @fileoverview AudioConnection - Microphone control component with real-time audio visualization
 * 
 * This module provides a compact, self-contained microphone control button that integrates
 * with LiveKit for real-time communication. It handles all aspects of microphone management
 * including toggle functionality, audio visualization, noise suppression, and user preferences.
 * 
 * Key Features:
 * - Microphone enable/disable toggle with visual feedback
 * - Real-time audio level visualization when active
 * - Automatic noise suppression processing
 * - User preference persistence via LiveKit
 * - Comprehensive error handling and logging
 * - LiveKit room context management
 * 
 * Architecture:
 * - AudioConnection (public): Wrapper that provides LiveKit room context
 * - AudioConnectionInner (private): Core logic that uses LiveKit hooks
 * 
 * @module AudioConnection
 * @requires livekit-client
 * @requires @livekit/components-react
 * @requires @instructure/ui
 * @requires @smarterservices/smarter-ui
 */

import { Track } from 'livekit-client';
import React from 'react';
import {
    useLocalParticipant,
    usePersistentUserChoices,
    RoomContext,
    useRoomContext,
    RoomAudioRenderer,
    useTracks,
} from '@livekit/components-react';
import { Button, Spinner } from '@instructure/ui';
import { IconButton, IconMicLine, IconMicOffLine, View, Tooltip, IconConfigureLine } from '@smarterservices/smarter-ui';
import { getStore, useAppStore, usePlayerStore } from '../../../stores/StoreContext';
import AudioVisualizer from './AudioVisualizer';

/**
 * AudioConnectionInner - Internal component that handles microphone control logic
 * 
 * Requirements:
 * 1. Starts disconnected - proctor cannot be heard
 * 2. Click to enable - opens audio, shows visualizer, proctor can speak
 * 3. Proctor can hear student but NOT themselves
 * 4. Click to disable - completely tears down mic, browser shows disconnected
 * 
 * Audio Processing:
 * Uses LiveKit's built-in WebRTC audio processing with:
 * - Noise suppression (filters background noise)
 * - Echo cancellation (prevents feedback)
 * - Auto gain control (normalizes volume levels)
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.showIcon - Whether to show microphone icon
 * @param {boolean} props.saveUserChoices - Whether to persist user preferences
 * @param {Function} props.onDeviceError - Callback for device errors
 * @param {string} props.audioInputDeviceId - Preferred audio input device ID
 * @param {string} props.audioOutputDeviceId - Preferred audio output device ID
 * 
 * @private
 */
const AudioConnectionInner = React.memo(function AudioConnectionInner({ showIcon, saveUserChoices, onDeviceError, audioInputDeviceId, audioOutputDeviceId }) {
    const [isConnected, setIsConnected] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [audioTrack, setAudioTrack] = React.useState(null);
    const { localParticipant } = useLocalParticipant();
    const AppStore = getStore('appStore');
    const ExamSessionStore = getStore('examSessionStore');
    const room = AppStore.room;
    const previousDeviceIdRef = React.useRef(audioInputDeviceId);

    const { saveAudioInputEnabled } = usePersistentUserChoices({
        preventSave: !saveUserChoices,
    });

    /**
     * Enable microphone - Acquire mic access and publish to room with audio processing
     */
    const enableMicrophone = React.useCallback(async () => {
        if (!room || !localParticipant) {
            console.error('[AudioConnection] No room or local participant available');
            return;
        }

        // Validate room context to ensure we are preventing cross-talk
        const expectedRoomSid = AppStore.sessionSid;
        const actualRoomSid = room?.roomInfo.name;
        const actualRoomLkSid = room?.roomInfo.sid;

        if (actualRoomSid !== expectedRoomSid) {
            console.error('[AudioConnection] ROOM MISMATCH DETECTED - Preventing microphone enable to avoid cross-talk', {
                expectedRoomSid,
                actualRoomSid,
                actualRoomLkSid,
                sessionSid: AppStore.sessionSid,
                localParticipantIdentity: localParticipant?.identity,
                issue: 'PR-3412'
            });

            // Prevent microphone from publishing to wrong room
            onDeviceError?.({
                type: 'room_mismatch',
                message: 'Room context mismatch detected. Please refresh the page.',
                expectedRoomSid,
                actualRoomSid
            });
            return;
        }

        const speakerCheckStep = ExamSessionStore.getMostRecentStep("SpeakerCheck");

        /*
                if (speakerCheckStep) {
                    // now if the metadata is that they opted out, we need to show a modal but allow them to contuie try try to connect if they want...
                    
                    if (speakerCheckStep.metadata.optedOut) {
                        AppStore.openSpeakerCheckModal();
                    }
                }
        */


        setIsLoading(true);
        try {
            // Enable microphone with LiveKit's built-in audio processing
            // This uses WebRTC's native audio constraints for optimal performance
            const audioOptions = {
                noiseSuppression: true,    // Filter background noise
                echoCancellation: true,     // Prevent feedback/echo
                autoGainControl: true,      // Normalize volume levels
            };

            // Use preferred device if provided, otherwise use system default
            if (audioInputDeviceId) {
                audioOptions.deviceId = audioInputDeviceId;
            }

            await localParticipant.setMicrophoneEnabled(true, audioOptions);

            // Wait a moment for the track to be published
            await new Promise(resolve => setTimeout(resolve, 200));

            // Get the published audio track for visualization
            if (localParticipant.audioTrackPublications?.size > 0) {
                const [[, publication]] = Array.from(
                    localParticipant.audioTrackPublications.entries()
                );

                if (publication?.track) {
                    setAudioTrack(publication.track);
                } else {
                    console.warn('[AudioConnection] Publication exists but no track found');
                }
            } else {
                console.warn('[AudioConnection] No audio track publications found after enabling microphone');
            }

            setIsConnected(true);
            await AppStore.setIsMicrophoneShareActive(true);
            saveAudioInputEnabled(true);

            // Update audio chat state and trigger event
            await ExamSessionStore.setAudioChatEnabled(true);

        } catch (err) {
            console.error('[AudioConnection] Failed to enable microphone:', err);
            setIsConnected(false);
            setAudioTrack(null);
            await AppStore.setIsMicrophoneShareActive(false);

            if (onDeviceError) {
                onDeviceError({ source: 'microphone', error: err });
            }
        } finally {
            setIsLoading(false);
        }
    }, [room, localParticipant, AppStore, ExamSessionStore, saveAudioInputEnabled, onDeviceError, audioInputDeviceId]);

    /**
     * Disable microphone - Completely tear down mic connection
     */
    const disableMicrophone = React.useCallback(async () => {
        if (!room || !localParticipant) return;

        try {
            // Disable and unpublish the microphone track
            await localParticipant.setMicrophoneEnabled(false);

            // Clear state
            setAudioTrack(null);
            setIsConnected(false);
            await AppStore.setIsMicrophoneShareActive(false);
            saveAudioInputEnabled(false);

            // Update audio chat state and trigger event
            await ExamSessionStore.setAudioChatEnabled(false);

        } catch (err) {
            console.error('[AudioConnection] Failed to disable microphone:', err);
        }
    }, [room, localParticipant, AppStore, ExamSessionStore, saveAudioInputEnabled]);

    /**
     * Handle microphone button click - Toggle connection
     */
    const handleMicrophoneClick = React.useCallback(async () => {
        // ensure we can communicate with the user, they must be past waiting for proctor...
        if (AppStore.attachedToProctorRoomSid && ExamSessionStore.isJourneyStepUnfinished('WaitingForProctor')) {
            // open a modal to inform user they need to start the checking process first
            AppStore.openCheckinRequiredModal();
            return;
        }

        if (isConnected) {
            await disableMicrophone();
        } else {
            await enableMicrophone();
        }
    }, [isConnected, enableMicrophone, disableMicrophone, AppStore, ExamSessionStore]);

    /**
     * Handle audio input device changes
     * If the microphone is active and the device changes, switch to the new device
     */
    React.useEffect(() => {
        const deviceChanged = previousDeviceIdRef.current !== audioInputDeviceId;

        if (deviceChanged && isConnected && audioInputDeviceId) {

            // Switch the active microphone device using LiveKit's switchActiveDevice
            const switchDevice = async () => {
                try {
                    // Use LiveKit's switchActiveDevice method which properly switches the underlying media device
                    await room.switchActiveDevice('audioinput', audioInputDeviceId);

                    // Wait a moment for the new track to be published
                    await new Promise(resolve => setTimeout(resolve, 200));

                    // Update the audio track reference for the visualizer
                    if (localParticipant.audioTrackPublications?.size > 0) {
                        const [[, publication]] = Array.from(
                            localParticipant.audioTrackPublications.entries()
                        );

                        if (publication?.track) {
                            setAudioTrack(publication.track);
                        }
                    }
                } catch (err) {
                    console.error('[AudioConnection] ❌ Failed to switch device:', err);
                }
            };

            switchDevice();
        }

        // Update the ref for next comparison
        previousDeviceIdRef.current = audioInputDeviceId;
    }, [audioInputDeviceId, isConnected, room, localParticipant]);

    /**
     * Cleanup on unmount - Always disable microphone
     * Use refs to prevent cleanup from running on state changes
     */
    const roomRef = React.useRef(room);
    const localParticipantRef = React.useRef(localParticipant);
    const isConnectedRef = React.useRef(isConnected);
    const appStoreRef = React.useRef(AppStore);

    // Update refs on every render
    React.useEffect(() => {
        roomRef.current = room;
        localParticipantRef.current = localParticipant;
        isConnectedRef.current = isConnected;
        appStoreRef.current = AppStore;
    });

    // Cleanup only on true unmount
    React.useEffect(() => {
        return () => {
            if (roomRef.current && localParticipantRef.current && isConnectedRef.current) {
                localParticipantRef.current.setMicrophoneEnabled(false).catch((err) => {
                    console.error('[AudioConnection] Error disabling microphone on unmount:', err);
                });
                appStoreRef.current.setIsMicrophoneShareActive(false);
            }
        };
    }, []); // Empty deps - only run on mount/unmount

    return (
        <View margin="none xx-small none none">
            <Tooltip
                renderTip={
                    <View as="div" width="175px">
                        {isConnected ? 'Disable microphone' : (
                            <>
                                Enable microphone - if you need to adjust your microphone settings use the options button ( <IconConfigureLine /> ) located next to the mute button..
                            </>
                        )}
                    </View>
                }
                placement="start"
            >
                <Button
                    size="small"
                    margin="none xx-small none none"
                    renderIcon={showIcon && (isLoading ? <Spinner size="x-small" renderTitle="Connecting..." /> : (isConnected ? <IconMicLine /> : <IconMicOffLine />))}
                    withBackground={true}
                    borderColor="primary"
                    color={isConnected ? 'success' : 'secondary'}
                    themeOverride={{
                        borderWidth: 0,
                    }}
                    onClick={handleMicrophoneClick}
                    interaction={isLoading ? 'disabled' : 'enabled'}
                >
                    {isConnected && audioTrack && (
                        <AudioVisualizer track={audioTrack} activeColor="secondary" barCount={3} />
                    )}
                </Button>
            </Tooltip>
        </View>
    );
});

/**
 * AudioConnection - Microphone control component with audio visualization
 * 
 * A compact microphone toggle button that displays real-time audio visualization
 * when the microphone is active. Automatically handles:
 * - LiveKit room context injection
 * - Microphone enable/disable toggle
 * - Real-time audio level visualization
 * - User preference persistence
 * - Device error handling
 * - Remote audio muting based on microphone enabled state
 * - Audio device selection (input and output)
 * 
 * Audio Behavior:
 * - Remote streams are MUTED by default (proctor cannot hear remote participants)
 * - When microphone component is enabled, remote audio is UNMUTED (proctor can hear)
 * - Clicking the button publishes local audio (remote participants can hear proctor)
 * - Local participant is always excluded from audio renderer (proctor doesn't hear themselves)
 * 
 * The component requires an active LiveKit room in the AppStore to function.
 * If no room is available, the component will render but may not function correctly.
 * 
 * @component
 * @example
 * ```jsx
 * // Basic usage
 * <AudioConnection />
 * 
 * // With custom configuration
 * <AudioConnection
 *   showIcon={true}
 *   saveUserChoices={true}
 *   audioInputDeviceId="device-id-123"
 *   audioOutputDeviceId="device-id-456"
 *   onDeviceError={(error) => console.error('Device error:', error)}
 * />
 * ```
 * 
 * @param {Object} props - Component props
 * @param {boolean} [props.showIcon=true] - Whether to display microphone icon in button
 * @param {boolean} [props.saveUserChoices=true] - Whether to persist microphone state to user preferences
 * @param {string} [props.audioInputDeviceId] - Preferred audio input device ID (falls back to system default)
 * @param {string} [props.audioOutputDeviceId] - Preferred audio output device ID (falls back to system default)
 * @param {Function} [props.onDeviceError] - Callback invoked when device errors occur
 * @param {Object} [props.onDeviceError.error] - Error object with source and error details
 * 
 * @returns {React.ReactElement} Microphone control button with audio visualization
 * 
 * @requires AppStore.room - LiveKit room instance from application store
 * @requires AppStore.enabledComponents.microphone - Whether microphone component is enabled
 * @requires AppStore.isMicrophoneShareActive - Current microphone state
 * @requires AppStore.setIsMicrophoneShareActive - Function to update microphone state
 */
export function AudioConnection({
    room: explicitRoom,  // PR-3412: Accept explicit room prop for defense in depth
    showIcon = true,
    saveUserChoices = true,
    audioInputDeviceId,
    audioOutputDeviceId,
    onDeviceError,
}) {
    const AppStore = getStore('appStore');

    // PR-3412: Use explicit room if provided, otherwise fall back to AppStore
    // Explicit room passing prevents React Context confusion
    const room = explicitRoom || AppStore.getState().room;

    // Check if microphone component is enabled - this controls remote audio
    const isMicrophoneComponentEnabled = useAppStore((state) => !!state.enabledComponents?.microphone);
    const volume = usePlayerStore((state) => state.volume);
    const muted = usePlayerStore((state) => state.muted);

    // Memoize room reference to prevent RoomContext from changing and causing re-renders
    const roomRef = React.useMemo(() => room, [room]);

    // Ensure we have a room before rendering
    if (!room) {
        console.warn('[AudioConnection] No LiveKit room available');
        return null;
    }

    // PR-3412: Wrap AudioConnectionInner with explicit RoomContext.Provider
    // This guarantees the correct room is used, bypassing any parent context

    return (
        <RoomContext.Provider value={room}>
            {isMicrophoneComponentEnabled && (
                <AudioConnectionInner
                    showIcon={showIcon}
                    saveUserChoices={saveUserChoices}
                    audioInputDeviceId={audioInputDeviceId}
                    audioOutputDeviceId={audioOutputDeviceId}
                    onDeviceError={onDeviceError}
                />
            )}
            {/* 
                RoomAudioRenderer controls whether we can HEAR remote participants.
                - muted={true} = Cannot hear remote participants (default)
                - muted={false} = Can hear remote participants (when microphone component is enabled)
                - excludeLocalParticipant={true} = Never hear ourselves
            */}
            <RoomAudioRenderer
                muted={muted}
                excludeLocalParticipant={true}
                volume={volume / 100}
            />
        </RoomContext.Provider>
    );
}

export default AudioConnection;
